/**
 * 员工资产领用记录页面
 * 用于查看员工的资产领用情况和历史记录
 */

import React, { useState, useEffect } from 'react';
import {
  Card, Select, Table, Tag, Space, Statistic, Row, Col, Empty, Timeline,
  Button, DatePicker, Tabs, message, Divider
} from 'antd';
import {
  UserOutlined, DesktopOutlined, ClockCircleOutlined, CheckCircleOutlined,
  SyncOutlined, ReloadOutlined, CalendarOutlined, TeamOutlined,
  FileTextOutlined, HistoryOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

// 资产状态映射
const ASSET_STATUS_MAP = {
  active: { text: '正常', color: 'green' },
  inactive: { text: '闲置', color: 'default' },
  maintenance: { text: '维护中', color: 'blue' },
  disposed: { text: '已报废', color: 'red' },
  lost: { text: '丢失', color: 'red' }
};

// 领用记录状态映射
const ASSIGNMENT_STATUS_MAP = {
  active: { text: '领用中', color: 'processing', icon: <SyncOutlined spin /> },
  returned: { text: '已归还', color: 'success', icon: <CheckCircleOutlined /> },
  transferred: { text: '已转移', color: 'default', icon: <ClockCircleOutlined /> }
};

const EmployeeAssetRecords = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState('current');

  // 统计数据
  const [statistics, setStatistics] = useState({
    total: 0,
    current: 0,
    returned: 0,
    transferred: 0
  });

  // 获取员工列表
  useEffect(() => {
    fetchEmployees();
  }, []);

  // 获取员工列表
  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees?status=probation,regular,active&limit=1000', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setEmployees(data.data.employees || []);
      }
    } catch (error) {
      console.error('获取员工列表失败:', error);
      message.error('获取员工列表失败');
    }
  };

  // 获取员工资产领用记录
  const fetchEmployeeAssets = async (employeeId, status = 'all') => {
    if (!employeeId) return;
    
    setLoading(true);
    try {
      // 使用V2资产API获取员工领用的资产
      const response = await fetch(`/api/v2/assets?employee_id=${employeeId}&limit=1000`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        let assets = data.data.assets || [];
        
        // 应用日期范围筛选
        if (dateRange && dateRange.length === 2) {
          assets = assets.filter(asset => {
            const updatedAt = dayjs(asset.updated_at);
            return updatedAt.isAfter(dateRange[0]) && updatedAt.isBefore(dateRange[1]);
          });
        }
        
        // 转换为统一的记录格式
        const records = assets.map(asset => ({
          id: asset.id,
          asset_id: asset.id,
          asset_name: asset.name,
          asset_no: asset.asset_no,
          asset_status: asset.status,
          type_name: asset.type_name,
          department_name: asset.department_name,
          assigned_at: asset.updated_at, // 使用更新时间作为领用时间
          returned_at: null, // 当前系统没有归还时间记录
          status: asset.current_holder ? 'active' : 'returned',
          notes: asset.notes
        }));
        
        setAssignments(records);
        
        // 计算统计数据
        const stats = {
          total: records.length,
          current: records.filter(r => r.status === 'active').length,
          returned: records.filter(r => r.status === 'returned').length,
          transferred: 0 // 当前系统没有转移记录
        };
        setStatistics(stats);
      }
    } catch (error) {
      console.error('获取资产记录失败:', error);
      message.error('获取资产记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 员工变更时获取数据
  useEffect(() => {
    if (selectedEmployee) {
      const status = activeTab === 'current' ? 'active' : 'all';
      fetchEmployeeAssets(selectedEmployee, status);
    }
  }, [selectedEmployee, dateRange, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // 当前领用资产表格列
  const currentColumns = [
    {
      title: '资产名称',
      dataIndex: 'asset_name',
      key: 'asset_name',
      render: (text, record) => (
        <Space>
          <DesktopOutlined style={{ color: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>{record.asset_no}</div>
          </div>
        </Space>
      )
    },
    {
      title: '资产类型',
      dataIndex: 'type_name',
      key: 'type_name',
      width: 120
    },
    {
      title: '资产状态',
      dataIndex: 'asset_status',
      key: 'asset_status',
      width: 100,
      render: (status) => {
        const config = ASSET_STATUS_MAP[status] || {};
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '领用时间',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      width: 180,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '领用天数',
      key: 'days',
      width: 100,
      render: (_, record) => {
        const days = dayjs().diff(dayjs(record.assigned_at), 'day');
        return <Tag color={days > 365 ? 'red' : days > 180 ? 'orange' : 'blue'}>{days} 天</Tag>;
      }
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text) => text || '-'
    }
  ];

  // 历史记录表格列
  const historyColumns = [
    {
      title: '资产名称',
      dataIndex: 'asset_name',
      key: 'asset_name',
      render: (text, record) => (
        <Space>
          <DesktopOutlined style={{ color: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>{record.asset_no}</div>
          </div>
        </Space>
      )
    },
    {
      title: '资产类型',
      dataIndex: 'type_name',
      key: 'type_name',
      width: 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = ASSIGNMENT_STATUS_MAP[status] || {};
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: '领用时间',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      width: 160,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '归还时间',
      dataIndex: 'returned_at',
      key: 'returned_at',
      width: 160,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '使用时长',
      key: 'duration',
      width: 100,
      render: (_, record) => {
        if (!record.returned_at) return '-';
        const days = dayjs(record.returned_at).diff(dayjs(record.assigned_at), 'day');
        return `${days} 天`;
      }
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text) => text || '-'
    }
  ];

  // 获取选中员工信息
  const currentEmployee = employees.find(e => e.id === selectedEmployee);

  return (
    <div style={{ padding: '10px 16px' }}>
      {/* 头部：员工选择 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="large">
              <div>
                <div style={{ marginBottom: 8, color: '#666' }}>选择员工</div>
                <Select
                  showSearch
                  placeholder="请选择员工"
                  style={{ width: 300 }}
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      <Space>
                        <UserOutlined />
                        {emp.name}
                        {emp.employee_no && <span style={{ color: '#999' }}>({emp.employee_no})</span>}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </div>

              {currentEmployee && (
                <div>
                  <Space size="large">
                    <Statistic
                      title="部门"
                      value={currentEmployee.department_name || '-'}
                      prefix={<TeamOutlined />}
                      valueStyle={{ fontSize: '16px' }}
                    />
                    <Statistic
                      title="职位"
                      value={currentEmployee.position_name || '-'}
                      prefix={<FileTextOutlined />}
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Space>
                </div>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              <div>
                <div style={{ marginBottom: 8, color: '#666' }}>时间范围</div>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  style={{ width: 260 }}
                />
              </div>
              <div style={{ paddingTop: 24 }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (selectedEmployee) {
                      const status = activeTab === 'current' ? 'active' : 'all';
                      fetchEmployeeAssets(selectedEmployee, status);
                    }
                  }}
                >
                  刷新
                </Button>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 统计卡片 */}
      {selectedEmployee && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总领用次数"
                value={statistics.total}
                prefix={<HistoryOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="当前领用"
                value={statistics.current}
                prefix={<SyncOutlined spin />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="已归还"
                value={statistics.returned}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#8c8c8c' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="已转移"
                value={statistics.transferred}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 主内容：资产列表 */}
      <Card>
        {!selectedEmployee ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="请先选择员工查看资产领用记录"
            style={{ padding: '60px 0' }}
          />
        ) : (
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
              {
                key: 'current',
                label: (
                  <span>
                    <SyncOutlined spin />
                    当前领用 ({statistics.current})
                  </span>
                ),
                children: (
                  <Table
                    columns={currentColumns}
                    dataSource={assignments.filter(a => a.status === 'active')}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{
                      emptyText: '暂无领用资产'
                    }}
                  />
                )
              },
              {
                key: 'history',
                label: (
                  <span>
                    <HistoryOutlined />
                    历史记录 ({statistics.total})
                  </span>
                ),
                children: (
                  <Table
                    columns={historyColumns}
                    dataSource={assignments}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{
                      emptyText: '暂无历史记录'
                    }}
                  />
                )
              },
              {
                key: 'timeline',
                label: (
                  <span>
                    <CalendarOutlined />
                    时间线视图
                  </span>
                ),
                children: (
                  <>
                    {assignments.length === 0 ? (
                      <Empty description="暂无记录" />
                    ) : (
                      <Timeline
                        mode="left"
                        style={{ marginTop: 24 }}
                        items={assignments.map(record => ({
                          color: record.status === 'active' ? 'blue' : record.status === 'returned' ? 'green' : 'gray',
                          label: dayjs(record.assigned_at).format('YYYY-MM-DD HH:mm'),
                          children: (
                            <div>
                              <div style={{ marginBottom: 8 }}>
                                <Space>
                                  <Tag color={ASSIGNMENT_STATUS_MAP[record.status]?.color}>
                                    {ASSIGNMENT_STATUS_MAP[record.status]?.text}
                                  </Tag>
                                  <strong>{record.asset_name}</strong>
                                  <span style={{ color: '#999' }}>({record.asset_no})</span>
                                </Space>
                              </div>
                              <div style={{ color: '#666', fontSize: '12px' }}>
                                类型: {record.type_name}
                                {record.returned_at && (
                                  <>
                                    <Divider type="vertical" />
                                    归还时间: {dayjs(record.returned_at).format('YYYY-MM-DD HH:mm')}
                                  </>
                                )}
                              </div>
                              {record.notes && (
                                <div style={{ marginTop: 4, color: '#999', fontSize: '12px' }}>
                                  备注: {record.notes}
                                </div>
                              )}
                            </div>
                          )
                        }))}
                      />
                    )}
                  </>
                )
              }
            ]}
          />

        )}
      </Card>
    </div>
  );
};

export default EmployeeAssetRecords;

